import { useState } from 'react'

import { Card } from '@joor/design-system'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'

import {
  BillingAddress,
  OrderStatus,
  ShippingAddress,
} from '__generated__/atlas-types'

import { formatNameWithCode } from 'utils/formatters'

import {
  isAccountTypeBrand as isAccountTypeBrandSelector,
  isEditBillingShippingRestricted as isEditBillingShippingRestrictedSelector,
} from 'store/currentUser/selectors'

import { OrderData } from '../../Overview'
import {
  AddressesSubmitParams,
  useAddressesWidgetData,
  useEditRestrict,
  useModalHandlers,
  useSubmitHandlers,
} from './addressesWidget.hooks'
import { EditAddressesModal } from './components/EditAddressesModal/EditAddressesModal'
import { useAddressModalData } from './components/EditAddressesModal/hooks/useAddressModalData'
import { EditTrackingNumberModal } from './components/EditTrackingNumberModal/EditTrackingNumberModal'
import { OrderShippingBillingInfo } from './components/OrderShippingBillingInfo/OrderShippingBillingInfo'
import {
  ProvidedAddress,
  UpdateAddressMismatchModal,
} from './components/WarningModals/UpdateAddressMismatchModal'
import { UpdateAddressStepsModal } from './components/WarningModals/UpdateAddressStepsModal'
import { UpdateAddressWarningModal } from './components/WarningModals/UpdateAddressWarningModal'
import { messages } from './constants/messages'
import { ReviewPageIds } from 'features/Orders/Review/review.ids'
import {
  formatBillingAddressToProvidedAddress,
  formatBillingAddressValuesToProvidedAddress,
  formatShippingAddressToProvidedAddress,
  formatShippingAddressValuesToProvidedAddress,
} from 'features/Orders/Review/utils/addresses.utils'

export enum AddressesWidgetTestIds {
  EditButton = 'addresses-edit-button',
  Loading = 'addresses-widget-loading-container',
  BillingAddressName = 'addresses-widget-billing-address-name',
  BillingAddressValue = 'addresses-widget-billing-address-value',
  ShippingAddressName = 'addresses-widget-shipping-address-name',
  ShippingAddressValue = 'addresses-widget-shipping-address-value',
}

type Props = {
  className?: string
  testId?: string
  brand?: NonNullable<OrderData['brand']>
  retailerProfile: OrderData['retailerProfile']
  orderStatus: OrderData['status']
  displayShippingCode: boolean
  isRetailerRestrictedFromEditing: boolean
  isOrderOrphan: boolean
  loading?: boolean
}

export type StepsModalPayload = {
  params: AddressesSubmitParams
  types: {
    shipping: 'csd' | 'local'
    billing: 'csd' | 'local'
  }
  csdAddresses?: {
    shipping?: ShippingAddress
    billing?: BillingAddress
  }
}

export const AddressesWidget = ({
  className = '',
  testId,
  brand,
  retailerProfile,
  orderStatus,
  displayShippingCode,
  isRetailerRestrictedFromEditing,
  isOrderOrphan,
  loading = false,
}: Props) => {
  const isAccountTypeBrand = useSelector(isAccountTypeBrandSelector)
  const isEditBillingShippingRestricted = useSelector(
    isEditBillingShippingRestrictedSelector,
  )
  const isAccountTypeRetailer = !isAccountTypeBrand

  const addressWidgetData = useAddressesWidgetData(
    {
      brand,
      orderStatus,
      displayShippingCode,
      isRetailerRestrictedFromEditing,
      isOrderOrphan,
      retailerProfile,
    },
    loading,
  )
  const modalData = useAddressModalData(
    addressWidgetData.brandId,
    addressWidgetData.retailerId,
  )

  const { isUpdating, submitAddress, submitStepAddresses } = useSubmitHandlers()
  const modalHandlers = useModalHandlers()

  const [paramsForWarningModal, setParamsForWarningModal] =
    useState<AddressesSubmitParams | null>(null)

  const [stepsModalPayload, setStepsModalPayload] =
    useState<StepsModalPayload>()

  const [providedCsdAddress, setProvidedCsdAddress] = useState<ProvidedAddress>(
    {} as ProvidedAddress,
  )
  const [providedValuesAddress, setProvidedValuesAddress] =
    useState<ProvidedAddress>({} as ProvidedAddress)

  const isEditRestricted = useEditRestrict(
    isAccountTypeBrand,
    addressWidgetData.orderStatus,
  )
  const isRetailerEditionRestricted =
    isAccountTypeRetailer && addressWidgetData.isRetailerRestrictedFromEditing

  const handleUpdateOrderOnly = async () => {
    if (paramsForWarningModal) {
      await submitAddress({ params: paramsForWarningModal })
    }
    setParamsForWarningModal(null)
    modalHandlers.closeAndResetModals()
  }

  const handleUpdateOrderAndAccount = async () => {
    if (paramsForWarningModal) {
      await submitAddress({
        params: paramsForWarningModal,
        overrideSourceAddress: true,
      })
    }
    setParamsForWarningModal(null)
    modalHandlers.closeAndResetModals()
  }

  const handleSubmitWithWarning = (params: AddressesSubmitParams) => {
    modalHandlers.openWarningModal({ which: 'warningUpdateOrder' })
    setParamsForWarningModal(params)
  }

  const handleSubmitWithMismatch = (
    params: AddressesSubmitParams,
    csdSelectedAddress:
      | ShippingAddress
      | BillingAddress
      | undefined = undefined,
  ) => {
    if (params.shipping) {
      const providedCsdAddress = formatShippingAddressToProvidedAddress(
        csdSelectedAddress as ShippingAddress,
      )

      const providedValuesAddress =
        formatShippingAddressValuesToProvidedAddress(params.shipping.values)

      setProvidedCsdAddress(providedCsdAddress)
      setProvidedValuesAddress(providedValuesAddress)

      modalHandlers.openWarningModal({ which: 'warningCompareAddress' })
      setParamsForWarningModal({ ...params, billing: undefined })
      return
    }

    if (params.billing) {
      const providedCsdAddress = formatBillingAddressToProvidedAddress(
        csdSelectedAddress as BillingAddress,
      )

      const providedValuesAddress = formatBillingAddressValuesToProvidedAddress(
        params.billing.values,
      )

      setProvidedCsdAddress(providedCsdAddress)
      setProvidedValuesAddress(providedValuesAddress)

      modalHandlers.openWarningModal({ which: 'warningCompareAddress' })
      setParamsForWarningModal({ ...params, shipping: undefined })
    }
  }

  const handleSubmitWithoutWarning = async (params: AddressesSubmitParams) => {
    await submitAddress({ params })
    modalHandlers.closeEditAddressModal()
  }

  const handleSubmitWithStepsModal = (payload: StepsModalPayload) => {
    modalHandlers.openWarningModal({ which: 'warningUpdateBothAddresses' })
    setStepsModalPayload(payload)
  }

  const submitFromStepsModal = async ({
    params,
    overrideShipping,
    overrideBilling,
  }: {
    params: AddressesSubmitParams
    overrideShipping: boolean
    overrideBilling: boolean
  }) => {
    await submitStepAddresses({ params, overrideShipping, overrideBilling })
    modalHandlers.closeAndResetModals()
  }

  return (
    <>
      <StyledCard
        className={className}
        testId={testId}
        title={messages.shippingAndBilling}
        actionButtonId={ReviewPageIds.ShipAndBillEditButtonId}
        {...(!isEditRestricted &&
          !isRetailerEditionRestricted && {
            actionButtonTestId: AddressesWidgetTestIds.EditButton,
            actionButtonLabel: 'Edit',
            onClickActionButton: modalHandlers.openEditAddressModal,
            disableActionButton:
              modalData.loadingAddressModalData ||
              addressWidgetData.loadingAddressesWidgetData,
          })}
      >
        <div
          data-testid={
            addressWidgetData.loadingAddressesWidgetData
              ? AddressesWidgetTestIds.Loading
              : undefined
          }
        >
          {isRetailerEditionRestricted && (
            <WarningText>{messages.warningText}</WarningText>
          )}
          <OrderShippingBillingInfo
            loading={addressWidgetData.loadingAddressesWidgetData}
            shippingAddress={addressWidgetData.orderShippingAddress}
            billingAddress={addressWidgetData.orderBillingAddress}
            shippingMethodName={formatNameWithCode(
              addressWidgetData.orderShippingMethod?.name,
              addressWidgetData.orderShippingMethod?.code,
            )}
            doorWithCode={formatNameWithCode(
              addressWidgetData.door?.name,
              addressWidgetData.door?.code,
            )}
            trackingNumber={addressWidgetData.trackingNumber}
            orderStatus={addressWidgetData.orderStatus}
            paymentMethodName={formatNameWithCode(
              addressWidgetData.paymentMethod?.name,
              addressWidgetData.paymentMethod?.code,
            )}
            showShippingCode={
              addressWidgetData.displayShippingCode && isAccountTypeBrand
            }
            orderTaxLabel={addressWidgetData.orderTax?.label}
            orderTaxNumber={addressWidgetData.orderTax?.number}
            orderBillingCode={
              addressWidgetData.orderBillingAddress?.sourceBillingAddress?.code
            }
          />
        </div>
      </StyledCard>
      {modalHandlers.isEditAddressModalOpen &&
        addressWidgetData.brandId &&
        addressWidgetData.orderStatus &&
        addressWidgetData.orderStatus === OrderStatus.SHIPPED &&
        !isEditBillingShippingRestricted && (
          <EditTrackingNumberModal
            modalData={modalData}
            orderData={addressWidgetData}
            onClose={modalHandlers.closeEditAddressModal}
          />
        )}
      {modalHandlers.isEditAddressModalOpen &&
        addressWidgetData.brandId &&
        addressWidgetData.orderStatus &&
        (addressWidgetData.orderStatus !== OrderStatus.SHIPPED ||
          (addressWidgetData.orderStatus === OrderStatus.SHIPPED &&
            isEditBillingShippingRestricted)) && (
          <EditAddressesModal
            modalData={modalData}
            orderData={addressWidgetData}
            isSubmitting={isUpdating}
            onSubmitWithMismatch={handleSubmitWithMismatch}
            onSubmitWithWarning={handleSubmitWithWarning}
            onSubmitWithoutWarning={handleSubmitWithoutWarning}
            onSubmitWithStepsModal={handleSubmitWithStepsModal}
            onClose={modalHandlers.closeEditAddressModal}
            readonly={isEditBillingShippingRestricted}
          />
        )}
      {modalHandlers.activeWarningModal === 'warningUpdateOrder' && (
        <UpdateAddressWarningModal
          isBrand={isAccountTypeBrand}
          isSubmitting={isUpdating}
          onContinue={handleUpdateOrderOnly}
          onClose={modalHandlers.closeWarningModal}
        />
      )}
      {modalHandlers.activeWarningModal === 'warningCompareAddress' && (
        <UpdateAddressMismatchModal
          addressType={
            paramsForWarningModal?.shipping
              ? messages.shipping
              : messages.billing
          }
          csdAddress={providedCsdAddress}
          editedAddress={providedValuesAddress}
          isSubmitting={isUpdating}
          onUpdateThisOrder={handleUpdateOrderOnly}
          onUpdateThisOrderAndAccount={handleUpdateOrderAndAccount}
          onClose={modalHandlers.closeWarningModal}
        />
      )}
      {modalHandlers.activeWarningModal === 'warningUpdateBothAddresses' &&
        stepsModalPayload && (
          <UpdateAddressStepsModal
            payload={stepsModalPayload}
            isSubmitting={isUpdating}
            onSubmit={submitFromStepsModal}
            onClose={modalHandlers.closeWarningModal}
          />
        )}
    </>
  )
}

const StyledCard = styled(Card)`
  section {
    margin: 0;
    padding: 10px;
  }
`

const WarningText = styled.span`
  color: #a34d00;
  font-size: var(--size-font-extrasmall);
  font-family: var(--font-family-alpha);
  font-weight: var(--font-weight-medium);
  line-height: 12px;
  letter-spacing: var(--letter-spacing-tight);
`
